import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Pasi } from "./Pasi";
import PiataPeBrief, { mixDinPiata, temaDinPiata, useStareaPietei } from "./PiataPeBrief";
import { Camp, Sursa } from "./campuri";
import { ETAJE_MAX, ETAJE_PRESET, SUBSOLURI, SUBSOL_PRESET, compuneRegim, imparteRegim,
         subsolDinActe,
         regimDinActe } from "./regim";
import { citesteLocalizare, salveazaLocalizare, TEMA_GOALA,
         type Calitate, type Localizare as Loc, type Tema,
         type RegimUrbanistic, type TipApartament } from "./dosarclient";
import * as I from "../components/icons";
import { BandaDoarCitire, useRolProiect } from "../components/rol";
import { t as tr, useT, useTn } from "../i18n";
import type { Cheie } from "../i18n";

// Pasul 3: CE se construiește pe lotul de la pasul 2.
//
// Despărțit de localizare fiindcă sunt două întrebări puse unor oameni
// diferiți, în momente diferite: pasul 2 e despre teren și ce permite
// certificatul — numere care ies din acte și se corectează —, pasul 3 e despre
// clădirea pe care o vrea beneficiarul. Într-un singur ecran, al doilea se
// citea ca o continuare a primului, adică tot ca niște date de completat.
//
// De aceea ecranul începe cu un câmp de text liber, nu cu un formular: prima
// dată se spune ce vrei, în cuvintele tale, și abia după aceea aceeași dorință
// se taie în bucățile pe care le poate mesteca un motor.

const TIPURI_DEZVOLTARE: [string, Cheie][] = [
  ["rezidential_colectiv", "loc.dezvoltare.rezidential_colectiv"],
  ["rezidential_individual", "loc.dezvoltare.rezidential_individual"],
  ["birouri", "loc.dezvoltare.birouri"],
  ["comercial", "loc.dezvoltare.comercial"],
  ["mixt", "loc.dezvoltare.mixt"],
  ["industrial", "loc.dezvoltare.industrial"],
];

// CE INTRĂ ÎN ABONAMENT. Deocamdată o singură categorie: rezidențial colectiv.
// Celelalte rămân în listă, scrise și stinse, nu șterse — o listă din care
// lipsesc înseamnă „nu se poate face asta", iar adevărul e „nu e în
// abonamentul tău", care e altceva și se poate schimba cu un telefon.
const IN_ABONAMENT = new Set(["rezidential_colectiv"]);

// Apartamentele se întreabă doar acolo unde există. Un depozit industrial cu
// un tabel de „70 % garsoniere" nu e o întrebare fără răspuns, e o întrebare
// care spune că ecranul nu ascultă.
const CU_APARTAMENTE = new Set(["rezidential_colectiv", "rezidential_individual", "mixt"]);

const NIVELURI: [string, Cheie, Cheie][] = [
  ["buget", "tema.nivel.buget", "tema.nivel.buget_sub"],
  ["standard", "tema.nivel.standard", "tema.nivel.standard_sub"],
  ["premium", "tema.nivel.premium", "tema.nivel.premium_sub"],
  ["lux", "tema.nivel.lux", "tema.nivel.lux_sub"],
];

type Capitol = Exclude<keyof Calitate, "nivel">;

const CAPITOLE: [Capitol, Cheie][] = [
  ["compartimentare", "tema.cap.compartimentare"],
  ["finisaje", "tema.cap.finisaje"],
  // Fațada nu mai e un rând aici: sistemul ei se alege în pasul 4, unde
  // standardul de mai sus doar îl preselectează.
  ["hvac", "tema.cap.hvac"],
  ["sanitare", "tema.cap.sanitare"],
  ["electrice", "tema.cap.electrice"],
];

const NIVEL_IMPLICIT = "standard";

/** Repartiția implicită a apartamentelor — PUNCT DE PLECARE declarat ca atare,
 *  nu o măsurătoare. Funcție, nu constantă: numele tipurilor se citesc în limba
 *  omului, iar o constantă de modul le-ar îngheța pe cele de la încărcarea
 *  fișierului. */
const implicite = (): TipApartament[] => [
  { tip: tr("loc.ap.doua_camere"), numar: null, procent: 0.7, suprafata_utila_mp: 55 },
  { tip: tr("loc.ap.garsoniera"), numar: null, procent: 0.2, suprafata_utila_mp: 37 },
  { tip: tr("loc.ap.trei_camere"), numar: null, procent: 0.1, suprafata_utila_mp: 78 },
];

/** Un rând de patru trepte: buget → standard → premium → lux.
 *
 *  Aceleași patru peste tot — global și pe fiecare capitol —, fiindcă altfel
 *  „premium la fațadă" și „premium peste tot" ar fi două scări care se citesc
 *  la fel și înseamnă altceva. */
function Trepte({ valoare, pe, mic }: {
  valoare: string; pe: (v: string) => void; mic?: boolean;
}) {
  const t = useT();
  return (
    <div className={`seg${mic ? " tm-trepte-mic" : ""}`}>
      {NIVELURI.map(([cheie, nume]) => (
        <button key={cheie} className={valoare === cheie ? "on" : ""}
                onClick={() => pe(cheie)}>{t(nume)}</button>
      ))}
    </div>
  );
}

export default function TemaProiect() {
  const t = useT();
  const tn = useTn();
  const { projectId } = useParams();
  const pid = projectId as string;
  const nav = useNavigate();
  const { potScrie } = useRolProiect();
  const [loc, setLoc] = useState<Loc | null>(null);
  const [gata, setGata] = useState(false);
  const [err, setErr] = useState("");
  const [avansat, setAvansat] = useState(false);
  // Textul liber se ține în ecran cât se scrie și se salvează când pleacă
  // focalizarea: o salvare pe fiecare tastă ar fi însemnat o cerere de rețea
  // la fiecare literă dintr-un paragraf.
  const [descriere, setDescriere] = useState<string | null>(null);

  useEffect(() => {
    citesteLocalizare(pid)
      .then(setLoc)
      .catch((e) => setErr(String(e)))
      .finally(() => setGata(true));
  }, [pid]);

  // ECRANUL SE SCHIMBĂ LA CLIC, nu la răspuns. Înainte un buton (standardul
  // clădirii, regimul) rămânea neapăsat cât dura salvarea plus recitirea
  // dosarului — secunde prin tunel (Cătălin, 7.10.2026). Acum starea se pune
  // întâi aici (`optimist`), salvarea pleacă în fundal, iar răspunsul
  // serverului se aplică numai dacă e al ULTIMEI salvări: două clicuri rapide
  // nu se mai calcă unul pe altul. La eroare se revine la ce e pe server.
  const ultimaSalvare = useRef(0);
  const salveaza = async (corectii: Record<string, unknown>,
                          optimist?: (l: Loc) => Loc) => {
    const nr = ++ultimaSalvare.current;
    if (optimist) setLoc((l) => (l ? optimist(l) : l));
    try {
      const l = await salveazaLocalizare(pid, corectii);
      if (nr === ultimaSalvare.current) setLoc(l);
      setErr("");
    } catch (e) {
      setErr(String(e));
      if (nr === ultimaSalvare.current) {
        citesteLocalizare(pid).then(setLoc).catch(() => undefined);
      }
    }
  };

  const tema: Tema = loc?.tema || TEMA_GOALA;
  const puneTema = (p: Partial<Tema>) =>
    salveaza({ tema: { ...tema, ...p } },
             (l) => ({ ...l, tema: { ...(l.tema || TEMA_GOALA), ...p } }));

  // PIAȚA DIN ZONĂ: starea căutării și profilul, cerute o dată pe pagină.
  // Recomandarea unei căutări încheiate intră singură în temă cât tema n-are
  // mixul omului (`temaDinPiata`); o propunere se scrie o singură dată, ca o
  // salvare picată să nu se reîncerce la fiecare desen.
  const piata = useStareaPietei(pid);
  const scrisDinPiata = useRef<string | null>(null);
  useEffect(() => {
    if (!potScrie || !loc || !CU_APARTAMENTE.has(tema.tip_dezvoltare || "rezidential_colectiv")) return;
    const p = temaDinPiata(tema, piata.profil);
    if (!p) return;
    const cheie = JSON.stringify(p);
    if (scrisDinPiata.current === cheie) return;
    scrisDinPiata.current = cheie;
    puneTema(p);
  // `tema` vine din `loc`; `puneTema` e alt obiect la fiecare desen
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [potScrie, loc, piata.profil]);

  // MĂRIMEA: ce e ales, ce spune certificatul, ce punem noi.
  //
  // Trei lucruri diferite, și ecranul le ține diferite. Presetul NU se scrie în
  // dosar la deschidere: un „schimbat de tine" apărut fiindcă cineva a deschis
  // o pagină e o minciună mică pe care nimeni n-o mai poate desface. Se scrie
  // la pornirea generării (din pasul Fațada, v. `regimEfectiv`), când chiar
  // devine decizia proiectului.
  const regimCU = useMemo(() => regimDinActe(loc?.regim_urbanistic), [loc?.regim_urbanistic]);
  const dinCU = useMemo(() => imparteRegim(regimCU), [regimCU]);
  const alesul = useMemo(() => imparteRegim(tema.regim_inaltime), [tema.regim_inaltime]);
  const subsol = alesul?.subsol ?? subsolDinActe(loc?.regim_urbanistic) ?? SUBSOL_PRESET;
  const etaje = alesul?.etaje ?? dinCU?.etaje ?? ETAJE_PRESET;
  const regimEfectiv = compuneRegim(subsol, etaje);
  const presetat = !alesul;
  // Regimul citit din acte pe care cele două liste nu-l pot scrie („P+2+M").
  const regimStrain = regimCU && !dinCU ? regimCU : null;
  // TERENUL ÎN MAI MULTE ZONE DE REGIM, cu înălțimi diferite: cifra de mai
  // sus e a zonei active, dar omul trebuie să vadă că pe M1 se poate altceva
  // decât pe Lc — generarea le va deosebi curând.
  const regimuriZone = useMemo(() => {
    const zone = loc?.regim_urbanistic?.zone ?? [];
    const perechi = zone
      .map((z) => ({ nume: z.nume, regim: z.regim_inaltime || regimDinActe(z as RegimUrbanistic) }))
      .filter((p): p is { nume: string; regim: string } => !!p.regim);
    return new Set(perechi.map((p) => p.regim)).size > 1 ? perechi : [];
  }, [loc?.regim_urbanistic?.zone]);
  const pesteCU = dinCU != null && etaje > dinCU.etaje;

  const puneRegim = (s: string, e: number) =>
    puneTema({ regim_inaltime: compuneRegim(s, e) });
  // BIFA C: regimul MINIM al unui corp, între parter și regimul ales. Se ține
  // ca șir, ca regimul ales («P+8»), și se taie la cel ales când acela scade.
  const minimAles = imparteRegim(tema.regim_minim);
  const etajeMin = Math.min(minimAles?.etaje ?? Math.max(0, etaje - 3), etaje);

  // CALITATEA. `nivel` e alegerea de fond; un capitol e `null` cât timp o
  // urmează. V. `Calitate` din `dosarclient.ts` pentru de ce nu se copiază.
  const calitate: Calitate = tema.calitate
    || { nivel: null, compartimentare: null, finisaje: null, fatada: null,
         hvac: null, sanitare: null, electrice: null };
  const nivel = calitate.nivel || NIVEL_IMPLICIT;
  const abateri = CAPITOLE.filter(([c]) => calitate[c] && calitate[c] !== nivel);

  const puneNivel = (v: string) =>
    // Nivelul global RESETEAZĂ capitolele. Altfel „am pus premium peste tot"
    // ar lăsa în urmă trei decizii vechi pe care nimeni nu și le amintește, iar
    // clădirea ar ieși altfel decât scrie pe ecran.
    puneTema({ calitate: { nivel: v, compartimentare: null, finisaje: null,
                           fatada: null, hvac: null, sanitare: null, electrice: null } });
  const puneCapitol = (c: Capitol, v: string) =>
    puneTema({ calitate: { ...calitate, nivel, [c]: v === nivel ? null : v } });

  const structura: TipApartament[] = tema.structura_apartamentelor?.length
    ? tema.structura_apartamentelor : implicite();
  const implicita = !tema.structura_apartamentelor?.length;
  const totalProcent = structura.reduce((s, a) => s + (a.procent || 0), 0);
  const puneStructura = (s: TipApartament[]) => puneTema({ structura_apartamentelor: s });
  const schimba = (i: number, p: Partial<TipApartament>) =>
    puneStructura(structura.map((a, j) => (j === i ? { ...a, ...p } : a)));

  const tip = tema.tip_dezvoltare || "rezidential_colectiv";

  if (!gata) {
    return (
      <div className="gol" style={{ height: "100%" }}>
        <span className="lbl">{t("loc.se_incarca")}</span>
      </div>
    );
  }

  return (
    <div className="tm">
      <div className="tm-cap">
        <Pasi pid={pid} activ="brief" />
        <div className="sp" />
        {/* Pasul următor e volumetria (schița de volume pe teren); fațada vine
            după ea, iar generarea pornește de acolo. */}
        <button className="btn btnp" style={{ height: 36, fontSize: 13 }}
                title={t("tema.la_volumetrie_titlu")}
                onClick={() => nav(`/p/${pid}/volumetrie`)}>
          {t("tema.la_volumetrie")} <I.Sageata size={14} />
        </button>
      </div>

      <BandaDoarCitire />
      {err && <div className="err">{err}</div>}

      {/* Două coloane: tema la stânga, piața din zonă la dreapta, lipită cât
          derulezi (Stefan, 31.10.2026, varianta B). Piața stă în afara
          fieldset-ului: «Vezi detalii» se deschide și pentru cine doar citește. */}
      <div className="tm-doua">
      <fieldset disabled={!potScrie} className="tm-camp">
      <div className="tm-col">

        {/* --- 1. În cuvintele tale ------------------------------------- */}
        <section className="card tm-card tm-spune">
          <b className="tm-titlu">{t("tema.spune.titlu")}</b>
          <textarea className="inp tm-text" rows={4}
                    placeholder={t("tema.spune.placeholder")}
                    value={descriere ?? tema.descriere ?? ""}
                    onChange={(e) => setDescriere(e.target.value)}
                    onBlur={() => {
                      if (descriere == null || descriere === (tema.descriere ?? "")) return;
                      puneTema({ descriere: descriere || null });
                    }} />
          <p className="note">{t("tema.spune.nota_imagini")}</p>
          <div className="tm-rand tm-rand-unul">
            <Camp eticheta="loc.tip_dezvoltare" obligatoriu
                  chip={<Sursa loc={loc} camp="tip_dezvoltare" />}>
              <select className="inp" value={tip}
                      onChange={(e) => puneTema({ tip_dezvoltare: e.target.value })}>
                {TIPURI_DEZVOLTARE.map(([k, v]) => (
                  <option key={k} value={k} disabled={!IN_ABONAMENT.has(k)}>
                    {t(v)}{IN_ABONAMENT.has(k) ? "" : ` — ${t("tema.fara_abonament")}`}
                  </option>))}
              </select>
            </Camp>
          </div>
        </section>

        {/* --- 2. Cât de mare ------------------------------------------- */}
        <section className="card tm-card">
          <div className="tm-card-cap">
            <b className="tm-titlu">{t("tema.marime.titlu")}</b>
            {presetat && <span className="chip c-out">{t("tema.preset")}</span>}
            <div className="sp" />
            <span className="tm-regim">{regimEfectiv}</span>
          </div>
          <div className="tm-rand">
            <Camp eticheta="loc.subsoluri">
              <select className="inp" value={subsol}
                      onChange={(e) => puneRegim(e.target.value, etaje)}>
                {SUBSOLURI.map(([pre, nume]) => (
                  <option key={pre || "fara"} value={pre}>{t(nume)}</option>))}
              </select>
            </Camp>
            <Camp eticheta="loc.etaje" chip={<Sursa loc={loc} camp="regim_inaltime" />}>
              <select className="inp" value={String(etaje)}
                      onChange={(e) => puneRegim(subsol, parseInt(e.target.value, 10))}>
                {Array.from({ length: ETAJE_MAX + 1 }, (_, n) => (
                  <option key={n} value={String(n)}>
                    {n === 0 ? t("loc.doar_parter") : t("loc.etaje_n", { n })}
                  </option>
                ))}
              </select>
            </Camp>
          </div>
          {/* VOLUMETRIA: două bife care merg la planul de situație și la
              arhitectură (v. `Tema.terase_retrase`, `Tema.inaltimi_diferite`). */}
          <div className="tm-rand tm-bife">
            <label className="tm-bifa">
              <input type="checkbox" checked={tema.terase_retrase ?? true}
                     onChange={(e) => puneTema({ terase_retrase: e.target.checked })} />
              <span>{t("tema.terase_retrase")}</span>
            </label>
            <label className="tm-bifa">
              <input type="checkbox" checked={tema.inaltimi_diferite ?? false}
                     onChange={(e) => puneTema({ inaltimi_diferite: e.target.checked })} />
              <span>{t("tema.inaltimi_diferite")}</span>
            </label>
            {/* BIFA C (Cătălin, 8.10.2026): corpuri cu înălțimi diferite între ele,
                cu regimul minim lângă ea. Cele trei bife sunt independente, iar
                orice combinație a lor merge; nebifată, totul e ca azi. */}
            <label className="tm-bifa">
              <input type="checkbox" checked={tema.corpuri_diferite ?? false}
                     onChange={(e) => puneTema({ corpuri_diferite: e.target.checked,
                                                 regim_minim: e.target.checked
                                                   ? compuneRegim(subsol, etajeMin) : tema.regim_minim })} />
              <span>{t("tema.corpuri_diferite")}</span>
            </label>
            {(tema.corpuri_diferite ?? false) && (
              <Camp eticheta="tema.regim_minim">
                <select className="inp" value={String(etajeMin)}
                        onChange={(e) => puneTema({ regim_minim: compuneRegim(subsol, parseInt(e.target.value, 10)) })}>
                  {Array.from({ length: etaje + 1 }, (_, n) => (
                    <option key={n} value={String(n)}>
                      {n === 0 ? t("loc.doar_parter") : t("loc.etaje_n", { n })}
                    </option>
                  ))}
                </select>
              </Camp>
            )}
          </div>
          {/* Ce spune certificatul stă lângă ce ai ales, nu în locul lui: peste
              regimul admis se poate construi cu derogare, iar ecranul n-are
              dreptul să hotărască în locul omului — dar are datoria să spună. */}
          <div className="tm-nota">
            {pesteCU
              ? <span className="chip c-amb">{t("tema.peste_cu", { regim: regimCU })}</span>
              : regimCU
                ? <span>{t("tema.din_cu", { regim: regimCU })}</span>
                : <span>{t("tema.fara_cu")}</span>}
            {regimStrain && <span>{t("loc.regim_din_acte", { regim: regimStrain })}</span>}
            {regimuriZone.length > 0 && (
              <span>{t("tema.regim_pe_zone", {
                lista: regimuriZone.map((p) => `${p.nume}: ${p.regim}`).join(" · ") })}</span>
            )}
          </div>
        </section>

        {/* --- 3. Nivelul de calitate ----------------------------------- */}
        <section className="card tm-card">
          <div className="tm-card-cap">
            <b className="tm-titlu">{t("tema.calitate.titlu")}</b>
            {tema.piata?.nivel != null && calitate.nivel === tema.piata.nivel && (
              <span className="chip c-acc">{t("cp.din_piata")}</span>
            )}
            {abateri.length > 0 && (
              <span className="chip c-met">
                {tn("tema.calitate.pe_capitole#n", abateri.length)}
              </span>
            )}
          </div>
          <div className="tm-nivele">
            {NIVELURI.map(([cheie, nume, sub]) => (
              <button key={cheie} type="button"
                      className={`tm-nivel${nivel === cheie ? " on" : ""}`}
                      onClick={() => puneNivel(cheie)}>
                <b>{t(nume)}</b>
                <span>{t(sub)}</span>
              </button>
            ))}
          </div>
          <button type="button" className="btn btng tm-avansat"
                  onClick={() => setAvansat(!avansat)}>
            <span className={`tm-caret${avansat ? " jos" : ""}`}><I.Sageata size={12} /></span>
            {t(avansat ? "tema.calitate.ascunde" : "tema.calitate.arata")}
          </button>
          {avansat && (
            <div className="tm-capitole">
              {CAPITOLE.map(([c, nume]) => (
                <div key={c} className="tm-capitol">
                  <span className="tm-capitol-nume">{t(nume)}</span>
                  {calitate[c] && calitate[c] !== nivel && (
                    <span className="chip c-met">{t("tema.calitate.aparte")}</span>
                  )}
                  <div className="sp" />
                  <Trepte mic valoare={calitate[c] || nivel}
                          pe={(v) => puneCapitol(c, v)} />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* --- 4. Apartamentele ----------------------------------------- */}
        {CU_APARTAMENTE.has(tip) && (
        <section className="card tm-card">
          <div className="tm-card-cap">
            <b className="tm-titlu">{t("loc.structura_ap")}</b>
            {implicita ? <span className="chip c-out">{t("loc.implicit")}</span>
              : mixDinPiata(tema) && <span className="chip c-acc">{t("cp.din_piata")}</span>}
            <div className="sp" />
            {Math.abs(totalProcent - 1) >= 0.001 && (
              <span className="chip c-bad">
                {t("loc.total_gresit", { procent: Math.round(totalProcent * 100) })}
              </span>
            )}
          </div>
          <div className="tm-ap">
            {structura.map((a, i) => (
              <div key={i} className="tm-ap-rand">
                <span className="tm-ap-procent">
                  <input className="inp mono" type="number" min={0} max={100}
                         value={Math.round((a.procent || 0) * 100)}
                         onChange={(e) => schimba(i, {
                           procent: Math.max(0, Math.min(100, Number(e.target.value))) / 100 })} />
                  <span className="mono">%</span>
                </span>
                <div className="tm-ap-nume">
                  <input value={a.tip} onChange={(e) => schimba(i, { tip: e.target.value })} />
                  <div className="tm-ap-mp">
                    <input type="number" min={0} placeholder="—"
                           value={a.suprafata_utila_mp ?? ""}
                           onChange={(e) => schimba(i, {
                             suprafata_utila_mp: e.target.value === "" ? null
                                                                       : Number(e.target.value) })} />
                    {t("loc.mp_utili")}
                  </div>
                </div>
                <div className="tm-ap-bara">
                  <span style={{ width: `${Math.min(100, (a.procent || 0) * 100)}%` }} />
                </div>
                <button className="fis-sterg" title={t("loc.scoate_tipul")}
                        style={{ opacity: 1 }}
                        onClick={() => puneStructura(structura.filter((_, j) => j !== i))}>
                  <I.X size={12} />
                </button>
              </div>
            ))}
            <div className="tm-ap-jos">
              <div className="sp" />
              <button className="btng btn" onClick={() => puneStructura(implicite())}>
                {t("loc.valori_implicite")}
              </button>
              <button className="btng btn"
                      onClick={() => puneStructura([...structura,
                        { tip: t("loc.ap.tip_nou"), numar: null, procent: 0,
                          suprafata_utila_mp: null }])}>
                <I.Plus size={12} />{t("loc.tip_nou")}
              </button>
            </div>
          </div>
        </section>
        )}

      </div>
      </fieldset>

      {CU_APARTAMENTE.has(tip) && (
        <aside className="tm-piata" aria-label={t("tema.piata.titlu")}>
          <PiataPeBrief projectId={pid} areAdresa={loc?.lat != null && loc?.lon != null}
                        potScrie={potScrie} tema={tema} piata={piata} laAplica={puneTema} />
        </aside>
      )}
      </div>
    </div>
  );
}
